import { z } from "zod";
import { jobIdSchema } from "./job-schemas";
import { providerBookingDetailSchema } from "./provider-booking-schemas";

const versionNoSchema = z.number().int().min(1).max(2_147_483_647);
const isoDateTimeSchema = z.string().datetime({ offset: true });
const optionalTrimmedInputTextSchema = z.string().trim().min(1).max(2_000).nullable();
const canonicalResponseTextSchema = z
  .string()
  .min(1)
  .max(2_000)
  .refine((value) => value === value.trim(), "Text must not contain surrounding whitespace");
const optionalCanonicalResponseTextSchema = canonicalResponseTextSchema.nullable();
const canonicalDisputeResponseTextSchema = z
  .string()
  .min(10)
  .max(2_000)
  .refine((value) => value === value.trim(), "Text must not contain surrounding whitespace");

function validateUniqueIds(
  ids: readonly string[],
  context: z.RefinementCtx,
  path: PropertyKey[] = [],
): void {
  const seen = new Set<string>();
  ids.forEach((id, index) => {
    if (seen.has(id)) {
      context.addIssue({
        code: "custom",
        path: [...path, index],
        message: "Service IDs must be unique",
      });
    }
    seen.add(id);
  });
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const rightIds = new Set(right);
  return rightIds.size === right.length && left.every((id) => rightIds.has(id));
}

export const providerCompletionAttestationSchema = z.literal("WORK_COMPLETED");

export const providerBookingCompletionInputSchema = z
  .strictObject({
    jobVersionNo: versionNoSchema,
    attestation: providerCompletionAttestationSchema,
    completionNote: optionalTrimmedInputTextSchema,
    completedServiceIds: z.array(jobIdSchema).min(1).max(100),
  })
  .superRefine((input, context) => {
    validateUniqueIds(input.completedServiceIds, context, ["completedServiceIds"]);
  });

export const jobCompletionEvidenceSchema = z
  .strictObject({
    id: jobIdSchema,
    jobId: jobIdSchema,
    jobVersionNo: versionNoSchema,
    attestation: providerCompletionAttestationSchema,
    completionNote: optionalCanonicalResponseTextSchema,
    completedServiceIds: z.array(jobIdSchema).min(1).max(100),
    completedAt: isoDateTimeSchema,
  })
  .superRefine((completion, context) => {
    validateUniqueIds(completion.completedServiceIds, context, ["completedServiceIds"]);
  });

export const providerBookingCompletionResponseSchema = z
  .strictObject({
    status: z.enum(["completed", "existing"]),
    booking: providerBookingDetailSchema,
    completion: jobCompletionEvidenceSchema,
  })
  .superRefine((response, context) => {
    if (response.booking.id !== response.completion.jobId) {
      context.addIssue({
        code: "custom",
        path: ["completion", "jobId"],
        message: "Completion must belong to the returned booking",
      });
    }
    if (
      response.booking.jobStatus !== "COMPLETED" ||
      response.booking.assignmentStatus !== "COMPLETED"
    ) {
      context.addIssue({
        code: "custom",
        path: ["booking", "jobStatus"],
        message: "Completion must return a completed booking",
      });
    }
    if (response.booking.jobVersionNo !== response.completion.jobVersionNo) {
      context.addIssue({
        code: "custom",
        path: ["completion", "jobVersionNo"],
        message: "Completion and booking versions must match",
      });
    }
    if (
      response.booking.endedAt === null ||
      Date.parse(response.booking.endedAt) !== Date.parse(response.completion.completedAt)
    ) {
      context.addIssue({
        code: "custom",
        path: ["completion", "completedAt"],
        message: "Completion time must match the booking end time",
      });
    }
    if (
      !sameIds(
        response.booking.services.map(({ serviceId }) => serviceId),
        response.completion.completedServiceIds,
      )
    ) {
      context.addIssue({
        code: "custom",
        path: ["completion", "completedServiceIds"],
        message: "Completion services must exactly match the booked services",
      });
    }
  });

export const postServiceOutcomeSchema = z.enum(["PENDING_CUSTOMER", "APPROVED", "DISPUTED"]);

export const completionDisputeReasonCodeSchema = z.enum([
  "SERVICE_INCOMPLETE",
  "QUALITY_ISSUE",
  "PROPERTY_DAMAGE",
  "SERVICE_NOT_PERFORMED",
  "OTHER",
]);

export const disputeJobCompletionInputSchema = z.strictObject({
  jobVersionNo: versionNoSchema,
  reasonCode: completionDisputeReasonCodeSchema,
  description: z.string().trim().min(10).max(2_000),
});

export const jobCompletionDisputeSchema = z.strictObject({
  id: jobIdSchema,
  reasonCode: completionDisputeReasonCodeSchema,
  description: canonicalDisputeResponseTextSchema,
  disputedAt: isoDateTimeSchema,
});

export const participantReviewInputSchema = z.strictObject({
  rating: z.number().int().min(1).max(5),
  comment: optionalTrimmedInputTextSchema,
});

export const participantReviewSideSchema = z.enum(["CUSTOMER", "PROVIDER"]);

export const participantReviewSchema = z.strictObject({
  id: jobIdSchema,
  reviewerSide: participantReviewSideSchema,
  rating: z.number().int().min(1).max(5),
  comment: optionalCanonicalResponseTextSchema,
  createdAt: isoDateTimeSchema,
});

export const actorPostServiceSchema = z
  .strictObject({
    jobId: jobIdSchema,
    jobVersionNo: versionNoSchema,
    completion: jobCompletionEvidenceSchema,
    outcome: postServiceOutcomeSchema,
    approvedAt: isoDateTimeSchema.nullable(),
    dispute: jobCompletionDisputeSchema.nullable(),
    customerReview: participantReviewSchema.nullable(),
    providerReview: participantReviewSchema.nullable(),
  })
  .superRefine((postService, context) => {
    if (
      postService.completion.jobId !== postService.jobId ||
      postService.completion.jobVersionNo !== postService.jobVersionNo
    ) {
      context.addIssue({
        code: "custom",
        path: ["completion"],
        message: "Completion must match the post-service job and version",
      });
    }

    if (postService.outcome === "PENDING_CUSTOMER") {
      if (postService.approvedAt !== null || postService.dispute !== null) {
        context.addIssue({
          code: "custom",
          path: ["outcome"],
          message: "A pending outcome cannot contain a terminal decision",
        });
      }
    } else if (postService.outcome === "APPROVED") {
      if (postService.approvedAt === null || postService.dispute !== null) {
        context.addIssue({
          code: "custom",
          path: ["outcome"],
          message: "An approved outcome requires only approval evidence",
        });
      }
    } else if (postService.approvedAt !== null || postService.dispute === null) {
      context.addIssue({
        code: "custom",
        path: ["outcome"],
        message: "A disputed outcome requires only dispute evidence",
      });
    }

    if (
      postService.approvedAt !== null &&
      Date.parse(postService.approvedAt) < Date.parse(postService.completion.completedAt)
    ) {
      context.addIssue({
        code: "custom",
        path: ["approvedAt"],
        message: "Approval cannot precede completion",
      });
    }
    if (
      postService.dispute !== null &&
      Date.parse(postService.dispute.disputedAt) < Date.parse(postService.completion.completedAt)
    ) {
      context.addIssue({
        code: "custom",
        path: ["dispute", "disputedAt"],
        message: "Dispute cannot precede completion",
      });
    }

    for (const [field, expectedSide, review] of [
      ["customerReview", "CUSTOMER", postService.customerReview],
      ["providerReview", "PROVIDER", postService.providerReview],
    ] as const) {
      if (review !== null && review.reviewerSide !== expectedSide) {
        context.addIssue({
          code: "custom",
          path: [field, "reviewerSide"],
          message: "Review side must match its post-service field",
        });
      }
      if (review !== null && postService.outcome !== "APPROVED") {
        context.addIssue({
          code: "custom",
          path: [field],
          message: "Reviews require an approved completion",
        });
      }
      if (
        review !== null &&
        postService.approvedAt !== null &&
        Date.parse(review.createdAt) < Date.parse(postService.approvedAt)
      ) {
        context.addIssue({
          code: "custom",
          path: [field, "createdAt"],
          message: "Review cannot precede approval",
        });
      }
    }

    const evidenceIds: Array<{ readonly id: string; readonly path: PropertyKey[] }> = [
      { id: postService.completion.id, path: ["completion", "id"] },
    ];
    if (postService.dispute !== null) {
      evidenceIds.push({ id: postService.dispute.id, path: ["dispute", "id"] });
    }
    if (postService.customerReview !== null) {
      evidenceIds.push({ id: postService.customerReview.id, path: ["customerReview", "id"] });
    }
    if (postService.providerReview !== null) {
      evidenceIds.push({ id: postService.providerReview.id, path: ["providerReview", "id"] });
    }
    const seenEvidenceIds = new Set<string>();
    for (const evidence of evidenceIds) {
      if (seenEvidenceIds.has(evidence.id)) {
        context.addIssue({
          code: "custom",
          path: evidence.path,
          message: "Completion, dispute, and review IDs must be distinct",
        });
      }
      seenEvidenceIds.add(evidence.id);
    }
  });

export const actorPostServiceResponseSchema = z.strictObject({
  status: z.literal("ok"),
  postService: actorPostServiceSchema,
});

export const disputeJobCompletionResponseSchema = z
  .strictObject({
    status: z.enum(["disputed", "existing"]),
    dispute: jobCompletionDisputeSchema,
    postService: actorPostServiceSchema,
  })
  .superRefine((response, context) => {
    const projected = response.postService.dispute;
    if (
      response.postService.outcome !== "DISPUTED" ||
      projected === null ||
      projected.id !== response.dispute.id ||
      projected.reasonCode !== response.dispute.reasonCode ||
      projected.description !== response.dispute.description ||
      Date.parse(projected.disputedAt) !== Date.parse(response.dispute.disputedAt)
    ) {
      context.addIssue({
        code: "custom",
        path: ["postService", "dispute"],
        message: "Dispute response must contain the same terminal post-service decision",
      });
    }
  });

export const participantReviewResponseSchema = z.strictObject({
  status: z.literal("ok"),
  review: participantReviewSchema,
});

export const submitParticipantReviewResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  review: participantReviewSchema,
});

export const providerNoShowEligibilitySchema = z.strictObject({
  jobId: jobIdSchema,
  eligibleAt: isoDateTimeSchema,
  available: z.boolean(),
});

export const providerNoShowEligibilityResponseSchema = z.strictObject({
  status: z.literal("ok"),
  eligibility: providerNoShowEligibilitySchema,
});

export const reportProviderNoShowInputSchema = z.strictObject({
  jobVersionNo: versionNoSchema,
});

export const providerNoShowReportSchema = z.strictObject({
  id: jobIdSchema,
  jobId: jobIdSchema,
  jobStatus: z.literal("CANCELLED"),
  jobVersionNo: versionNoSchema,
  reportedAt: isoDateTimeSchema,
});

export const reportProviderNoShowResponseSchema = z.strictObject({
  status: z.enum(["reported", "existing"]),
  report: providerNoShowReportSchema,
});

export const postServiceIdempotencyKeySchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/u);

export type ProviderBookingCompletionInput = z.infer<typeof providerBookingCompletionInputSchema>;
export type JobCompletionEvidence = z.infer<typeof jobCompletionEvidenceSchema>;
export type ProviderBookingCompletionResponse = z.infer<
  typeof providerBookingCompletionResponseSchema
>;
export type PostServiceOutcome = z.infer<typeof postServiceOutcomeSchema>;
export type CompletionDisputeReasonCode = z.infer<typeof completionDisputeReasonCodeSchema>;
export type DisputeJobCompletionInput = z.infer<typeof disputeJobCompletionInputSchema>;
export type JobCompletionDispute = z.infer<typeof jobCompletionDisputeSchema>;
export type ParticipantReviewInput = z.infer<typeof participantReviewInputSchema>;
export type ParticipantReviewSide = z.infer<typeof participantReviewSideSchema>;
export type ParticipantReview = z.infer<typeof participantReviewSchema>;
export type ActorPostService = z.infer<typeof actorPostServiceSchema>;
export type DisputeJobCompletionResponse = z.infer<typeof disputeJobCompletionResponseSchema>;
export type ParticipantReviewResponse = z.infer<typeof participantReviewResponseSchema>;
export type SubmitParticipantReviewResponse = z.infer<typeof submitParticipantReviewResponseSchema>;
export type ProviderNoShowEligibility = z.infer<typeof providerNoShowEligibilitySchema>;
export type ReportProviderNoShowInput = z.infer<typeof reportProviderNoShowInputSchema>;
export type ProviderNoShowReport = z.infer<typeof providerNoShowReportSchema>;
export type ReportProviderNoShowResponse = z.infer<typeof reportProviderNoShowResponseSchema>;
